import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import AppLayout from './AppLayout';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import RouteFallback from '@/shared/components/RouteFallback';
import { getBrowserRouterFactory } from '@/lib/sentry';

const createBrowserRouter = getBrowserRouterFactory();

// Light pages stay eager — small, often visited, low bundle cost.
import { DashboardPage } from '@/features/dashboard';
import { TransactionsPage } from '@/features/transactions';
import { BudgetsPage } from '@/features/budgets';

// Heavy routes split out. Hitting /reports won't load the pdfjs + xlsx bundles,
// hitting /networth won't load the reports chart bundle, etc.
const BillsPage = lazy(() => import('@/features/bills/pages/BillsPage'));
const CategoriesPage = lazy(() => import('@/features/categories/pages/CategoriesPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const SavingsPage = lazy(() => import('@/features/savings/pages/SavingsPage'));
const NetWorthPage = lazy(() => import('@/features/networth/pages/NetWorthPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const ShoppingPage = lazy(() => import('@/features/shopping/pages/ShoppingPage'));
const PrivacyPolicyPage = lazy(() => import('@/shared/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/shared/pages/TermsOfServicePage'));

const wrap = (name, element) => (
  <ErrorBoundary name={name}>
    <Suspense fallback={<RouteFallback />}>{element}</Suspense>
  </ErrorBoundary>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary name="the app">
        <AppLayout />
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: wrap('dashboard', <DashboardPage />) },
      { path: 'transactions', element: wrap('transactions', <TransactionsPage />) },
      { path: 'budget', element: wrap('budget', <BudgetsPage />) },
      { path: 'bills', element: wrap('bills', <BillsPage />) },
      { path: 'list', element: wrap('shopping list', <ShoppingPage />) },
      { path: 'categories', element: wrap('categories', <CategoriesPage />) },
      { path: 'overview', element: wrap('year overview', <ReportsPage />) },
      { path: 'savings', element: wrap('savings', <SavingsPage />) },
      { path: 'networth', element: wrap('net worth', <NetWorthPage />) },
      { path: 'settings', element: wrap('settings', <SettingsPage />) },
      { path: 'privacy', element: wrap('privacy policy', <PrivacyPolicyPage />) },
      { path: 'terms', element: wrap('terms of service', <TermsOfServicePage />) },
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
